// What the pages compute from the achievement catalog and one account's
// record (achievements.md §2, §7, §8; R939).
//
// The catalog rides the client bundle, so every page already knows every
// code, its category and its ladder. What a route carries is the one thing
// the bundle cannot know: which grades THIS account holds. This file joins
// the two, and it draws nothing — the hub RECORD grid, the RANKS board and
// the in-game STANDINGS journey list all read it, and a test reads it with
// no DOM.
//
// Two rules hold everywhere here:
//
//  1. **A hidden category is hidden from the VIEW and not from the
//     bundle** (§8). The codes are public; a page filters what an account
//     may see, and the service filters what it answers.
//  2. **A recognition is worth one badge and no points** (§6). It counts
//     one toward what a category holds and never six.

import {
  ACHIEVEMENTS, ACHIEVEMENT_CATEGORIES, ACHIEVEMENT_GRADES, ACHIEVEMENT_POINTS,
  ACHIEVEMENT_ROTATION_COUNT, type AchievementRow,
} from '../gen/presentation';
import { achievements as A } from '../gen/strings';
import { groupThousands } from '../locale/group';
import { SEC_PER_HOUR, SEC_PER_MINUTE } from '../locale/clock';
import type { AccountAchievements } from '../net/api';

/** The rows of one category, in the catalog's own order. */
const BY_CATEGORY = new Map<string, AchievementRow[]>();
const BY_CODE = new Map<string, AchievementRow>();
for (const row of ACHIEVEMENTS) {
  BY_CODE.set(row.code, row);
  const list = BY_CATEGORY.get(row.category);
  if (list) list.push(row);
  else BY_CATEGORY.set(row.category, [row]);
}

/** Every row of one category, in the catalog's order. */
export function rowsOfCategory(id: string): readonly AchievementRow[] {
  return BY_CATEGORY.get(id) ?? [];
}

/**
 * The depth zone one stored step of a row stands at: 0 for none, and 1
 * (Littoral) to the count of zones (Challenger) for a step reached. The
 * twin of `progress.ZoneOf` (achievements.md §2, R1552).
 *
 * The record stores the STEP, and every name, ink, pip and point a page
 * draws comes from the zone. A lifetime row with fewer steps than zones
 * ends at Challenger, and the zones it skips come out of the middle: five
 * steps read 1, 2, 3, 4, 6. A journey row keeps its steps as its zones. A
 * code this build does not know keeps its step.
 */
export function zoneOf(row: AchievementRow | null | undefined, step: number): number {
  if (step <= 0) return 0;
  const zones = ACHIEVEMENT_GRADES.length;
  if (!row) return Math.min(step, zones);
  const n = row.thresholds.length;
  if (n === 0) return 0;
  const at = Math.min(step, n);
  if (row.kind === 'lifetime' && at === n) return zones;
  return Math.min(at, zones);
}

/** What one stored step of a row is worth: the points of its zone (§7). */
export function stepPoints(row: AchievementRow | null | undefined, step: number): number {
  const zone = zoneOf(row, step);
  return zone > 0 ? ACHIEVEMENT_POINTS[Math.min(zone, ACHIEVEMENT_POINTS.length) - 1]! : 0;
}

/** The catalog row of one code, or null for a code this build lacks. */
export function rowByCode(code: string): AchievementRow | null {
  return BY_CODE.get(code) ?? null;
}

/** How many grades one row can reach. A recognition is one badge. */
export function gradesOf(row: AchievementRow): number {
  return row.kind === 'recognition' ? 1 : row.thresholds.length;
}

/**
 * A category the pages hide from an account that holds no key to it.
 *
 * The test is the CATALOG's and not a name: a category every row of which
 * counts inside a gated mode is a gated category. Today that is `event`
 * alone, and a later gated area needs no edit here.
 */
export function isHiddenCategory(id: string): boolean {
  const rows = rowsOfCategory(id);
  return rows.length > 0 && rows.every((r) => r.mode === 'event');
}

/** Code to the grade the account holds, 1 to 6. */
export function gradeMap(a: AccountAchievements | null | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const g of a?.grades ?? []) {
    const held = out.get(g.code) ?? 0;
    if (g.grade > held) out.set(g.code, g.grade);
  }
  return out;
}

/** The newest instant this account reached any grade, or 0 for none. */
export function newestGradeAt(a: AccountAchievements | null | undefined): number {
  let out = 0;
  for (const g of a?.grades ?? []) if (g.reachedAt > out) out = g.reachedAt;
  for (const r of a?.recognitions ?? []) if (r.grantedAt > out) out = r.grantedAt;
  return out;
}

/** One tile of the RECORD grid. */
export interface CategoryView {
  id: string;
  /** The deepest zone the account holds anywhere in the category, 0 for
   *  none (R1552). The tile's badge stands at it. */
  best: number;
  /** How many grades the account has reached in the category. */
  reached: number;
  /** How many it could reach. */
  possible: number;
  /** Whether the category is gated. */
  hidden: boolean;
}

/** What one category holds for this account. */
export function categoryView(id: string, a: AccountAchievements | null | undefined): CategoryView {
  const held = gradeMap(a);
  const awards = new Set((a?.recognitions ?? []).map((r) => r.code));
  let best = 0;
  let reached = 0;
  let possible = 0;
  for (const row of rowsOfCategory(id)) {
    possible += gradesOf(row);
    if (row.kind === 'recognition') {
      if (awards.has(row.code)) {
        reached += 1;
        best = Math.max(best, 1);
      }
      continue;
    }
    const g = held.get(row.code) ?? 0;
    reached += g;
    best = Math.max(best, zoneOf(row, g));
  }
  return { id, best, reached, possible, hidden: isHiddenCategory(id) };
}

/** The categories a page draws, gated ones dropped unless `entitled`. */
export function visibleCategories(entitled: boolean): readonly string[] {
  return ACHIEVEMENT_CATEGORIES
    .map((c) => c.id)
    .filter((id) => entitled || !isHiddenCategory(id));
}

/** What the account's points add up to from the grades it holds: the
 *  points of the zone each row stands at, as the service sums them. The
 *  service is the authority and states its own figure; this is what a page
 *  falls back on when the answer carries none. */
export function pointsOf(a: AccountAchievements | null | undefined): number {
  if (a && a.points > 0) return a.points;
  let out = 0;
  for (const [code, step] of gradeMap(a)) out += stepPoints(rowByCode(code), step);
  return out;
}

/** How many grades the account holds, over every category. */
export function gradeCount(a: AccountAchievements | null | undefined): number {
  let out = 0;
  for (const [, grade] of gradeMap(a)) out += grade;
  return out + (a?.recognitions.length ?? 0);
}

/**
 * The journey rows that are live this season (R939).
 *
 * A journey row is either PERMANENT (`rotation === -1`) or a member of one
 * rotation set, and set N is live while `season % ACHIEVEMENT_ROTATION_COUNT
 * === N`. A row joins the list only in a world of its own family, which is
 * what `family` says: `''` for every core world, `'event'` for an event
 * world, `'clanwar'` for a Clan War world, `'arcade'` for the arcade. The
 * server counts a journey row on its own family alone
 * (`progress.ActiveJourney`), so a core row in an event or a Clan War world
 * would be an objective nobody can move. Neither family holds a journey
 * row, so both worlds draw the empty list (R1511).
 *
 * It is a pure function of the catalog, the season and the mode, so the
 * STANDINGS tab draws the list with no call and no server: only the VALUES
 * need the record, and the list stands without them.
 */
export function activeJourney(season: number, family: Family = ''): AchievementRow[] {
  const set = ((season % ACHIEVEMENT_ROTATION_COUNT) + ACHIEVEMENT_ROTATION_COUNT)
    % ACHIEVEMENT_ROTATION_COUNT;
  return ACHIEVEMENTS.filter((r) => r.kind === 'journey'
    && (r.rotation === -1 || r.rotation === set)
    && r.mode === family);
}

/** A category family: the `mode` column of a catalog row. */
export type Family = AchievementRow['mode'];

/** The category family one world's mode belongs to (achievements.md §2).
 *  A Clan War world feeds its own family and no core row (R1511). */
export function familyOf(mode: string): Family {
  if (mode === 'event') return 'event';
  if (mode === 'clanwar') return 'clanwar';
  if (mode === 'arcade') return 'arcade';
  return '';
}

/** The next threshold above the grade held, or undefined for a finished
 *  ladder and for a recognition. */
export function nextThreshold(row: AchievementRow, grade: number): number | undefined {
  if (row.kind === 'recognition') return undefined;
  return row.thresholds[Math.max(0, grade)];
}

/** The unit of a metric that counts the player's own seconds (R949). */
const UNIT_SEC = 'sec';

/**
 * One value of one achievement row, as a page prints it (R949).
 *
 * A `sec` metric counts WALL seconds, and nobody reads a year as
 * 3 600 000. So the span reads as whole minutes under one hour and as
 * hours with one decimal above it — the arcade's fastest win is a matter
 * of minutes and a lifetime at sea is a matter of hundreds of hours, and
 * one rule serves both. Every other unit is a grouped whole number; a
 * `mask` value already arrives as its bit count (R947).
 *
 * The unit rides the generated catalog row, so no page keeps a list of
 * which metrics are time.
 *
 * `group` is the thousands grouper. The hub's ladder column measures in a
 * monospaced face and hands in its own (`hubstate.groupDigits`); every
 * other surface takes the thin space of `locale/group.ts` (R687).
 */
export function formatMetricValue(
  unit: string, value: number, group: (n: number) => string = groupThousands,
): string {
  if (unit !== UNIT_SEC) return group(value);
  // Under one minute the seconds print, because a non-zero total that
  // reads "0 min" says "nothing counted", which is the one thing a value
  // must never say (R939).
  if (value < SEC_PER_MINUTE) return A.value_seconds({ value: Math.round(value) });
  if (value < SEC_PER_HOUR) {
    return A.value_minutes({ value: Math.round(value / SEC_PER_MINUTE) });
  }
  // Rounded in TENTHS first, so 3599.9 s never prints as "1.0 h" beside a
  // whole hour that is not yet reached, and 35 999 s never prints "9.10".
  const tenths = Math.round(value / (SEC_PER_HOUR / 10));
  return A.value_hours({ value: `${group(Math.floor(tenths / 10))}.${tenths % 10}` });
}

/** What one row's current value reads as, or undefined where the account
 *  has none: a recognition, or a metric the record does not carry. */
export function rowValue(
  row: AchievementRow, totals: Record<string, number>,
  group?: (n: number) => string,
): string | undefined {
  if (row.metric === '') return undefined;
  const now = totals[row.metric];
  if (now === undefined) return undefined;
  return formatMetricValue(row.unit, now, group);
}

/** What one row's next threshold reads as, or undefined for a finished
 *  ladder and for a recognition. */
export function rowNext(
  row: AchievementRow, grade: number, group?: (n: number) => string,
): string | undefined {
  const next = nextThreshold(row, grade);
  if (next === undefined) return undefined;
  return formatMetricValue(row.unit, next, group);
}

// ---------------------------------------------------------------------------
// The in-game record (R939)
// ---------------------------------------------------------------------------
//
// The hub has a cache (`HubData`); the game page has none, and the
// STANDINGS tab is redrawn four times a second. So the record is asked for
// ONE time per session and held here. Nothing polls: a journey value that
// is a few minutes old is the truth of the last flush anyway, and the
// fly-in is what tells a player about a grade the instant it is reached.
//
// A page with no meta server behind it — the direct mode, a dev server on
// its own — never answers, and the holder then says so: the STANDINGS list
// draws every objective of the season with no value against it, which is
// still the useful half of the list.

/** What the record call last answered. */
type RecordState = 'none' | 'asking' | 'ready' | 'failed';

let recordState: RecordState = 'none';
let record: AccountAchievements | null = null;
/** The record generation: a reset starts a new one, and an answer of an
 * older generation lands nowhere, so the old season's record never
 * replaces the new one (R2236). */
let generation = 0;

/** The record this session holds, or null before one lands. */
export function heldRecord(): AccountAchievements | null {
  return record;
}

/** Whether the record call failed, which the page states rather than hides. */
export function recordFailed(): boolean {
  return recordState === 'failed';
}

/**
 * Asks for the record once a session.
 *
 * `fetchRecord` is handed in, so this leaf pulls no route into a unit test.
 * `onLand` runs when the answer arrives, for a caller that draws on its own
 * clock and wants one extra pass.
 */
export function askRecord(
  fetchRecord: () => Promise<AccountAchievements>,
  onLand?: () => void,
): void {
  if (recordState !== 'none') return;
  recordState = 'asking';
  const mine = generation;
  fetchRecord().then(
    (a) => {
      if (mine !== generation) return;
      record = a;
      recordState = 'ready';
      onLand?.();
    },
    () => {
      if (mine !== generation) return;
      recordState = 'failed';
      onLand?.();
    },
  );
}

/** Forgets the record. A season roll and a test call it. */
export function resetRecord(): void {
  generation++;
  recordState = 'none';
  record = null;
}
